import { useState, useEffect, useCallback } from "react";
import { Routes, Route, Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { 
  LayoutDashboard, Building, Grid3X3, MapPin, LogOut, Plus, 
  Pencil, Trash2, Search, ChevronLeft, ChevronRight, Menu, X, Star
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { businessesApi, categoriesApi, departmentsApi, statsApi, uploadApi, contactApi } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, MessageSquare, Eye, Mail as MailIcon } from "lucide-react";

// Dashboard Overview
const DashboardOverview = () => {
  const [stats, setStats] = useState({ businesses: 0, categories: 0, departments: 0, unread_messages: 0 });

  useEffect(() => {
    const loadStats = async () => {
      try {
        const res = await statsApi.get();
        setStats(res.data);
      } catch (error) {
        console.error("Error loading stats:", error);
      }
    };
    loadStats();
  }, []);

  return (
    <div data-testid="dashboard-overview">
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Tableau de bord</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="stats-card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-[#FFD700]/20 flex items-center justify-center">
              <Building className="w-6 h-6 text-[#FFD700]" />
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">{stats.businesses}</p>
              <p className="text-sm text-slate-600">Entreprises</p>
            </div>
          </div>
        </div>
        <div className="stats-card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-[#2563EB]/20 flex items-center justify-center">
              <Grid3X3 className="w-6 h-6 text-[#2563EB]" />
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">{stats.categories}</p>
              <p className="text-sm text-slate-600">Catégories</p>
            </div>
          </div>
        </div>
        <div className="stats-card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
              <MapPin className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">{stats.departments}</p>
              <p className="text-sm text-slate-600">Départements</p>
            </div>
          </div>
        </div>
        <Link to="/admin/messages" className="stats-card hover:border-[#2563EB] transition-colors">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center relative">
              <MessageSquare className="w-6 h-6 text-purple-500" />
              {stats.unread_messages > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {stats.unread_messages}
                </span>
              )}
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">{stats.unread_messages}</p>
              <p className="text-sm text-slate-600">Messages non lus</p>
            </div>
          </div>
        </Link>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4">Actions rapides</h2>
        <div className="flex flex-wrap gap-3">
          <Link to="/admin/businesses/new">
            <Button className="bg-[#FFD700] text-slate-900 hover:bg-[#E6C200]">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une entreprise
            </Button>
          </Link>
          <Link to="/admin/businesses">
            <Button variant="outline">
              <Building className="w-4 h-4 mr-2" />
              Gérer les entreprises
            </Button>
          </Link>
          <Link to="/admin/messages">
            <Button variant="outline">
              <MessageSquare className="w-4 h-4 mr-2" />
              Voir les messages
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Business Form Component
const BusinessForm = ({ business, onSubmit, loading }) => {
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    phone: "",
    email: "",
    website: "",
    address: "",
    city: "",
    postal_code: "",
    department_code: "",
    category_id: "",
    logo_url: "",
    banner_url: "",
    photos: [],
    latitude: "",
    longitude: "",
    additional_cities: [],
    additional_activities: [],
    has_website_creation: false,
    ...business
  });
  const [additionalCitiesText, setAdditionalCitiesText] = useState(business?.additional_cities?.join(", ") || "");
  const [additionalActivitiesText, setAdditionalActivitiesText] = useState(business?.additional_activities?.join(", ") || "");
  const [photosText, setPhotosText] = useState(business?.photos?.join("\n") || "");
  const [newPhotoUrl, setNewPhotoUrl] = useState("");

  useEffect(() => {
    const loadData = async () => {
      try {
        const [catRes, deptRes] = await Promise.all([
          categoriesApi.getAll(),
          departmentsApi.getAll(),
        ]);
        setCategories(catRes.data);
        setDepartments(deptRes.data);
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };
    loadData();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      additional_cities: additionalCitiesText.split(",").map(s => s.trim()).filter(Boolean),
      additional_activities: additionalActivitiesText.split(",").map(s => s.trim()).filter(Boolean),
      photos: photosText.split("\n").map(s => s.trim()).filter(Boolean),
    };
    onSubmit(data);
  };

  const [uploading, setUploading] = useState(false);

  const addPhoto = () => {
    if (newPhotoUrl && newPhotoUrl.startsWith("http")) {
      setPhotosText(prev => prev ? `${prev}\n${newPhotoUrl}` : newPhotoUrl);
      setNewPhotoUrl("");
    }
  };

  const removePhoto = (urlToRemove) => {
    const photos = photosText.split("\n").filter(url => url.trim() !== urlToRemove);
    setPhotosText(photos.join("\n"));
  };

  const handleFileUpload = async (e, targetField) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez sélectionner une image (JPG, PNG, GIF, WEBP)");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5 Mo");
      return;
    }

    setUploading(true);
    try {
      const res = await uploadApi.uploadFile(file);
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      
      if (targetField === "logo") {
        setFormData(prev => ({ ...prev, logo_url: imageUrl }));
      } else if (targetField === "banner") {
        setFormData(prev => ({ ...prev, banner_url: imageUrl }));
      } else if (targetField === "photo") {
        setPhotosText(prev => prev ? `${prev}\n${imageUrl}` : imageUrl);
      }
      toast.success("Image uploadée avec succès !");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Erreur lors de l'upload de l'image");
    } finally {
      setUploading(false);
      e.target.value = ""; // Reset input
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="business-form">
      {/* Basic Info */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-4">Informations générales</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label className="form-label">Nom de l'entreprise *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="h-12"
              data-testid="business-name-input"
            />
          </div>
          <div>
            <Label className="form-label">Catégorie *</Label>
            <Select
              value={formData.category_id || "placeholder"}
              onValueChange={(value) => setFormData({ ...formData, category_id: value === "placeholder" ? "" : value })}
            >
              <SelectTrigger className="h-12" data-testid="business-category-select">
                <SelectValue placeholder="Sélectionner une catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="placeholder" disabled>Sélectionner une catégorie</SelectItem>
                {categories.filter(cat => cat.id).map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="form-label">Téléphone *</Label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
              className="h-12"
              data-testid="business-phone-input"
            />
          </div>
          <div>
            <Label className="form-label">Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="h-12"
            />
          </div>
          <div>
            <Label className="form-label">Site web</Label>
            <Input
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              placeholder="https://"
              className="h-12"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="form-label">Description *</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              rows={4}
              data-testid="business-description-input"
            />
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-4">Adresse</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label className="form-label">Adresse *</Label>
            <Input
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              required
              className="h-12"
              data-testid="business-address-input"
            />
          </div>
          <div>
            <Label className="form-label">Ville *</Label>
            <Input
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              required
              className="h-12"
              data-testid="business-city-input"
            />
          </div>
          <div>
            <Label className="form-label">Code postal *</Label>
            <Input
              value={formData.postal_code}
              onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
              required
              className="h-12"
            />
          </div>
          <div>
            <Label className="form-label">Département *</Label>
            <Select
              value={formData.department_code || "placeholder"}
              onValueChange={(value) => setFormData({ ...formData, department_code: value === "placeholder" ? "" : value })}
            >
              <SelectTrigger className="h-12" data-testid="business-department-select">
                <SelectValue placeholder="Sélectionner un département" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="placeholder" disabled>Sélectionner un département</SelectItem>
                {departments.filter(dept => dept.code).map((dept) => (
                  <SelectItem key={dept.code} value={dept.code}>
                    {dept.code} - {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="form-label">Villes complémentaires</Label>
            <Input
              value={additionalCitiesText}
              onChange={(e) => setAdditionalCitiesText(e.target.value)}
              placeholder="Paris, Lyon, Marseille"
              className="h-12"
            />
            <p className="text-xs text-slate-500 mt-1">Séparées par des virgules</p>
          </div>
          <div>
            <Label className="form-label">Latitude</Label>
            <Input
              type="number"
              step="any"
              value={formData.latitude}
              onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
              className="h-12"
              placeholder="48.8566"
            />
          </div>
          <div>
            <Label className="form-label">Longitude</Label>
            <Input
              type="number"
              step="any"
              value={formData.longitude}
              onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
              className="h-12"
              placeholder="2.3522"
            />
          </div>
        </div>
      </div>

      {/* Media */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-4">Médias</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="form-label">Logo</Label>
            <div className="flex gap-2">
              <Input
                type="url"
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                placeholder="https://... ou uploadez"
                className="h-12 flex-1"
              />
              <label className="h-12 px-4 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-md flex items-center gap-2 cursor-pointer">
                <Upload className="w-4 h-4" />
                <span className="text-sm font-medium">Parcourir</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileUpload(e, "logo")}
                  disabled={uploading}
                />
              </label>
            </div>
            {formData.logo_url && (
              <img src={formData.logo_url} alt="Logo preview" className="mt-2 w-20 h-20 object-cover rounded-lg border" />
            )}
          </div>
          <div>
            <Label className="form-label">Bannière</Label>
            <div className="flex gap-2">
              <Input
                type="url"
                value={formData.banner_url}
                onChange={(e) => setFormData({ ...formData, banner_url: e.target.value })}
                placeholder="https://... ou uploadez"
                className="h-12 flex-1"
              />
              <label className="h-12 px-4 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-md flex items-center gap-2 cursor-pointer">
                <Upload className="w-4 h-4" />
                <span className="text-sm font-medium">Parcourir</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileUpload(e, "banner")}
                  disabled={uploading}
                />
              </label>
            </div>
            {formData.banner_url && (
              <img src={formData.banner_url} alt="Banner preview" className="mt-2 w-full h-24 object-cover rounded-lg border" />
            )}
          </div>
        </div>

        {/* Photos Gallery */}
        <div className="mt-6">
          <Label className="form-label">Photos de l'entreprise</Label>
          <div className="flex gap-2 mb-3">
            <Input
              type="url"
              value={newPhotoUrl}
              onChange={(e) => setNewPhotoUrl(e.target.value)}
              placeholder="https://... (URL de la photo)"
              className="h-12 flex-1"
              data-testid="new-photo-input"
            />
            <Button 
              type="button" 
              onClick={addPhoto}
              className="h-12 bg-[#2563EB] hover:bg-[#1d4ed8]"
              data-testid="add-photo-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              URL
            </Button>
            <label className="h-12 px-4 bg-[#FFD700] hover:bg-[#E6C200] text-slate-900 font-bold rounded-md flex items-center gap-2 cursor-pointer">
              <Upload className="w-4 h-4" />
              Parcourir
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFileUpload(e, "photo")}
                disabled={uploading}
              />
            </label>
          </div>
          {uploading && (
            <div className="text-sm text-blue-600 mb-2">Upload en cours...</div>
          )}
          
          {/* Photos preview grid */}
          {photosText && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              {photosText.split("\n").filter(Boolean).map((url, idx) => (
                <div key={idx} className="relative group">
                  <img 
                    src={url} 
                    alt={`Photo ${idx + 1}`} 
                    className="w-full h-24 object-cover rounded-lg border border-slate-200"
                    onError={(e) => { e.target.src = "https://via.placeholder.com/150?text=Image"; }}
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(url)}
                    className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    data-testid={`remove-photo-${idx}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-slate-500 mt-2">Ajoutez plusieurs photos pour présenter votre entreprise</p>
        </div>
      </div>

      {/* Additional */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-4">Informations complémentaires</h3>
        <div className="space-y-4">
          <div>
            <Label className="form-label">Activités complémentaires</Label>
            <Input
              value={additionalActivitiesText}
              onChange={(e) => setAdditionalActivitiesText(e.target.value)}
              placeholder="Chauffage, Climatisation, Dépannage"
              className="h-12"
            />
            <p className="text-xs text-slate-500 mt-1">Séparées par des virgules</p>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={formData.is_featured || false}
              onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
              data-testid="featured-switch"
            />
            <Label>Mettre à la une sur la page d'accueil</Label>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={formData.has_website_creation}
              onCheckedChange={(checked) => setFormData({ ...formData, has_website_creation: checked })}
              data-testid="website-creation-switch"
            />
            <Label>Création de site internet (service proposé)</Label>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <Link to="/admin/businesses">
          <Button type="button" variant="outline">Annuler</Button>
        </Link>
        <Button 
          type="submit" 
          className="bg-[#FFD700] text-slate-900 hover:bg-[#E6C200]"
          disabled={loading}
          data-testid="business-submit-btn"
        >
          {loading ? "Enregistrement..." : (business ? "Mettre à jour" : "Créer l'entreprise")}
        </Button>
      </div>
    </form>
  );
};

// Business List
const BusinessList = () => {
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [bizRes, catRes] = await Promise.all([
        businessesApi.getAll(page, 30, searchQuery),
        categoriesApi.getAll(),
      ]);
      setBusinesses(bizRes.data.businesses);
      setTotal(bizRes.data.total);
      setTotalPages(bizRes.data.total_pages);
      setCategories(catRes.data);
    } catch (error) {
      console.error("Error loading businesses:", error);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleDelete = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette entreprise ?")) return;
    try {
      await businessesApi.delete(id);
      setBusinesses(businesses.filter(b => b.id !== id));
      setTotal(t => t - 1);
      toast.success("Entreprise supprimée");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleToggleFeatured = async (id) => {
    try {
      const res = await businessesApi.toggleFeatured(id);
      setBusinesses(businesses.map(b => 
        b.id === id ? { ...b, is_featured: res.data.is_featured } : b
      ));
      toast.success(res.data.is_featured ? "Entreprise mise à la une" : "Entreprise retirée de la une");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || "N/A";
  };

  return (
    <div data-testid="business-list">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-900">
          Entreprises
          <span className="text-base font-normal text-slate-500 ml-2">({total})</span>
        </h1>
        <Link to="/admin/businesses/new">
          <Button className="bg-[#FFD700] text-slate-900 hover:bg-[#E6C200]" data-testid="add-business-btn">
            <Plus className="w-4 h-4 mr-2" />
            Ajouter
          </Button>
        </Link>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Rechercher par nom, ville ou téléphone..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-10 h-12"
            data-testid="admin-search-input"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">Chargement...</div>
        ) : businesses.length === 0 ? (
          <div className="p-8 text-center text-slate-500">Aucune entreprise trouvée</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-4 font-semibold text-slate-700 w-10">
                    <Star className="w-4 h-4 text-slate-400" />
                  </th>
                  <th className="text-left p-4 font-semibold text-slate-700">Entreprise</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Catégorie</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Ville</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Téléphone</th>
                  <th className="text-right p-4 font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {businesses.map((business) => (
                  <tr key={business.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-4">
                      <button
                        onClick={() => handleToggleFeatured(business.id)}
                        className="transition-colors"
                        title={business.is_featured ? "Retirer de la une" : "Mettre à la une"}
                        data-testid={`featured-toggle-${business.id}`}
                      >
                        <Star 
                          className={`w-5 h-5 ${business.is_featured ? "fill-[#FFD700] text-[#FFD700]" : "text-slate-300 hover:text-[#FFD700]"}`} 
                        />
                      </button>
                    </td>
                    <td className="p-4">
                      <div className="font-medium text-slate-900">{business.name}</div>
                    </td>
                    <td className="p-4 text-slate-600">{getCategoryName(business.category_id)}</td>
                    <td className="p-4 text-slate-600">{business.city}</td>
                    <td className="p-4 text-slate-600 font-mono text-sm">{business.phone}</td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/admin/businesses/${business.id}/edit`}>
                          <Button variant="ghost" size="sm" data-testid={`edit-${business.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDelete(business.id)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`delete-${business.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-slate-600">
            Page {page} sur {totalPages} ({total} entreprises)
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(1)}
              data-testid="pagination-first"
            >
              Début
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              data-testid="pagination-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="px-3 py-1 text-sm font-medium text-slate-700">{page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
              data-testid="pagination-next"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page === totalPages}
              onClick={() => setPage(totalPages)}
              data-testid="pagination-last"
            >
              Fin
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// New Business Page
const NewBusinessPage = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      await businessesApi.create(data);
      toast.success("Entreprise créée avec succès !");
      navigate("/admin/businesses");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="new-business-page">
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Nouvelle entreprise</h1>
      <BusinessForm onSubmit={handleSubmit} loading={loading} />
    </div>
  );
};

// Edit Business Page
const EditBusinessPage = () => {
  const { id } = useParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const loadBusiness = async () => {
      try {
        const res = await businessesApi.getOne(id);
        setBusiness(res.data);
      } catch (error) {
        toast.error("Entreprise non trouvée");
        navigate("/admin/businesses");
      } finally {
        setLoading(false);
      }
    };
    loadBusiness();
  }, [id, navigate]);

  const handleSubmit = async (data) => {
    setSaving(true);
    try {
      await businessesApi.update(id, data);
      toast.success("Entreprise mise à jour !");
      navigate("/admin/businesses");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div data-testid="edit-business-page">
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Modifier l'entreprise</h1>
      <BusinessForm business={business} onSubmit={handleSubmit} loading={saving} />
    </div>
  );
};

// Messages Page
const MessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [businesses, setBusinesses] = useState({});
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const loadMessages = async () => {
      try {
        const res = await contactApi.getAll({ page: 1, limit: 50 });
        setMessages(res.data.messages);
        setTotal(res.data.total);
        
        // Load business names
        const bizIds = [...new Set(res.data.messages.map(m => m.business_id))];
        const bizMap = {};
        for (const bizId of bizIds) {
          try {
            const bizRes = await businessesApi.getOne(bizId);
            bizMap[bizId] = bizRes.data.name;
          } catch { bizMap[bizId] = "Entreprise supprimée"; }
        }
        setBusinesses(bizMap);
      } catch (error) {
        console.error("Error loading messages:", error);
      } finally {
        setLoading(false);
      }
    };
    loadMessages();
  }, []);

  const handleMarkRead = async (id) => {
    try {
      await contactApi.markRead(id);
      setMessages(messages.map(m => m.id === id ? { ...m, read: true } : m));
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce message ?")) return;
    try {
      await contactApi.delete(id);
      setMessages(messages.filter(m => m.id !== id));
      setTotal(t => t - 1);
      toast.success("Message supprimé");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <div data-testid="messages-page">
      <h1 className="text-2xl font-bold text-slate-900 mb-6">
        Messages de contact
        <span className="text-base font-normal text-slate-500 ml-2">({total})</span>
      </h1>

      {loading ? (
        <div className="text-center py-8">Chargement...</div>
      ) : messages.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucun message</h3>
          <p className="text-slate-600">Les messages de contact des visiteurs apparaîtront ici.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`bg-white rounded-xl border p-5 ${msg.read ? "border-slate-200" : "border-[#2563EB] border-l-4"}`}
              data-testid={`message-${msg.id}`}
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-slate-900">{msg.sender_name}</span>
                    {!msg.read && (
                      <span className="px-2 py-0.5 bg-[#2563EB] text-white text-xs font-semibold rounded-full">Nouveau</span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <MailIcon className="w-3 h-3" />
                      <a href={`mailto:${msg.sender_email}`} className="hover:text-[#2563EB]">{msg.sender_email}</a>
                    </span>
                    {msg.sender_phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        <a href={`tel:${msg.sender_phone}`} className="hover:text-[#2563EB]">{msg.sender_phone}</a>
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs text-slate-400">{formatDate(msg.created_at)}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Pour : <Link to={`/entreprise/${msg.business_id}`} className="text-[#2563EB] hover:underline" target="_blank">
                      {businesses[msg.business_id] || "..."}
                    </Link>
                  </p>
                </div>
              </div>
              <p className="text-slate-700 whitespace-pre-line text-sm mb-3">{msg.message}</p>
              <div className="flex items-center gap-2">
                {!msg.read && (
                  <Button variant="outline" size="sm" onClick={() => handleMarkRead(msg.id)} data-testid={`mark-read-${msg.id}`}>
                    <Eye className="w-3 h-3 mr-1" />
                    Marquer comme lu
                  </Button>
                )}
                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(msg.id)} data-testid={`delete-msg-${msg.id}`}>
                  <Trash2 className="w-3 h-3 mr-1" />
                  Supprimer
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Admin Dashboard
export default function AdminDashboard() {
  const { admin, logout, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated()) {
      navigate("/admin/login");
    }
  }, [loading, isAuthenticated, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#FFD700] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated()) {
    return null;
  }

  const navItems = [
    { path: "/admin", icon: LayoutDashboard, label: "Tableau de bord" },
    { path: "/admin/businesses", icon: Building, label: "Entreprises" },
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Déconnexion réussie");
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      {/* Mobile Header */}
      <div className="lg:hidden bg-slate-900 text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#FFD700] rounded-lg flex items-center justify-center">
            <span className="text-slate-900 font-bold">A</span>
          </div>
          <span className="font-bold">Admin</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`admin-sidebar w-64 min-h-screen fixed lg:static inset-y-0 left-0 z-50 transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 transition-transform`}>
          {/* Logo */}
          <div className="p-6 border-b border-slate-800">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#FFD700] rounded-lg flex items-center justify-center">
                <span className="text-slate-900 font-bold text-xl">P</span>
              </div>
              <span className="font-bold text-lg">PartenaireEntreprises</span>
            </Link>
          </div>

          {/* Admin Info */}
          <div className="p-4 border-b border-slate-800">
            <p className="text-sm text-slate-400">Connecté en tant que</p>
            <p className="font-medium truncate">{admin?.name}</p>
          </div>

          {/* Navigation */}
          <nav className="p-4 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || 
                (item.path !== "/admin" && location.pathname.startsWith(item.path));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`admin-nav-item ${isActive ? "active" : ""}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
            <button
              onClick={handleLogout}
              className="admin-nav-item w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Déconnexion
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8 min-h-screen">
          <Routes>
            <Route path="/" element={<DashboardOverview />} />
            <Route path="/businesses" element={<BusinessList />} />
            <Route path="/businesses/new" element={<NewBusinessPage />} />
            <Route path="/businesses/:id/edit" element={<EditBusinessPage />} />
          </Routes>
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
